import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiRefreshCw, FiSearch, FiSliders, FiX } from "react-icons/fi";
import Header from "../../components/home/Header";
import Nav from "../../components/home/Nav";
import { useAllCategoriesQuery, useFilterProductsQuery } from "../../store/services/homeProducts";
import ProductCard from "../../components/home/ProductCard";
import ProductSkeleton from "../../components/home/ProductSkeleton";
import ProductFilters from "../../components/home/ProductFilters";
import { toPersianNumber } from "../../utils/persian";

const commonSizes = ["Free", "35", "36", "37", "38", "39", "40", "41", "42"];

const splitValues = (value = "") =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const toFaText = (value = "") =>
  String(value ?? "").replace(/[0-9]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

const SizeFinder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const category = searchParams.get("category") || "";
  const size = searchParams.get("size") || "";
  const sort = searchParams.get("sort") || "latest";
  const brand = searchParams.get("brand") || "";
  const color = searchParams.get("color") || "";
  const minPrice = searchParams.get("min_price") || "";
  const maxPrice = searchParams.get("max_price") || "";

  const available = searchParams.get("available") ?? "1";

  const [filters, setFilters] = useState({
    q: "",
    category,
    fixedCategory: "",
    brand,
    color,
    size,
    min_price: minPrice,
    max_price: maxPrice,
    available,
    sort,
    page: 1
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      category,
      brand,
      color,
      size,
      min_price: minPrice,
      max_price: maxPrice,
      available,
      sort,
      page: 1
    }));
  }, [category, brand, color, size, minPrice, maxPrice, available, sort]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      category,
      brand,
      color,
      size,
      min_price: minPrice,
      max_price: maxPrice,
      sort,
      available: "1",
      page: 1
    }));
  }, [category, brand, color, size, minPrice, maxPrice, sort]);

  const { data: categoriesData } = useAllCategoriesQuery();
  const categories = categoriesData?.categories || [];

  const selectedCategoryValues = splitValues(filters.category);
  const selectedSizeValues = splitValues(filters.size);

  const selectedCategories = categories.filter((item) =>
    selectedCategoryValues.includes(String(item.slug || item.name || item.id))
  );

  const selectedCategoryTitle = selectedCategories.length
    ? selectedCategories.map((item) => item.name).join("، ")
    : "";

  const queryParams = useMemo(
    () => ({
      category: filters.category,
      brand: filters.brand,
      color: filters.color,
      size: filters.size,
      min_price: filters.min_price,
      max_price: filters.max_price,
      available: filters.available,
      sort: filters.sort,
      per_page: 48,
      page: 1
    }),
    [filters]
  );

  const { data, isFetching } = useFilterProductsQuery(queryParams, {
    skip: !filters.size
  });

  const products = Array.isArray(data?.products) ? data.products : [];
  const resultCount = Number(data?.count ?? data?.total ?? products.length ?? 0);
  const hasProducts = products.length > 0;

  const updateFilters = (updater) => {
    setFilters((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const params = new URLSearchParams();

      if (next.category) params.set("category", next.category);
      if (next.size) params.set("size", next.size);
      if (next.brand) params.set("brand", next.brand);
      if (next.color) params.set("color", next.color);
      if (next.min_price) params.set("min_price", next.min_price);
      if (next.max_price) params.set("max_price", next.max_price);
      if (next.available) params.set("available", next.available);
      if (next.sort && next.sort !== "latest") params.set("sort", next.sort);

      setSearchParams(params, { replace: true });

      return {
        ...next,
        available: next.available ?? "",
        page: 1
      };
    });
  };

  const toggleSize = (value) => {
    updateFilters((prev) => {
      const current = splitValues(prev.size);
      const nextSizes = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      return {
        ...prev,
        size: nextSizes.join(","),
        page: 1
      };
    });
  };

  const clearSize = () => {
    updateFilters((prev) => ({
      ...prev,
      size: "",
      page: 1
    }));
  };

  const clearAll = () => {
    updateFilters({
      q: "",
      category: "",
      fixedCategory: "",
      brand: "",
      color: "",
      size: "",
      min_price: "",
      max_price: "",
      available: "",
      sort: "latest",
      page: 1
    });
  };

  const allCommonSelected =
    commonSizes.every((item) => selectedSizeValues.includes(item)) &&
    selectedSizeValues.length >= commonSizes.length;

  const title = selectedSizeValues.length
    ? allCommonSelected
      ? "محصولات موجود برای همه سایزها"
      : `محصولات موجود برای سایز ${selectedSizeValues.map((item) => (item === "Free" ? "Free" : toFaText(item))).join("، ")}`
    : "جستجو بر اساس سایز";

  const subtitle = selectedCategoryTitle ? `در دسته‌بندی ${selectedCategoryTitle}` : "در همه دسته‌بندی‌ها";

  return (
    <>
      <Nav />

      <div className="mt-[76px]">
        <Header>سایز من</Header>
      </div>

      <section className="shop-page size-finder-page-modern">
        <div className="site-container">
          <div className="size-finder-modern-hero">
            <div>
              <span>جستجوی سریع با سایز</span>
              <h1>{title}</h1>
              <p>{subtitle}؛ فقط کالاهای موجود نمایش داده می‌شوند.</p>
            </div>

            <div className="size-finder-quick-panel">
              <strong>سایزهای پرکاربرد</strong>

              <div className="size-finder-size-pills">
                {commonSizes.map((item) => {
                  const active = selectedSizeValues.includes(item);

                  return (
                    <button
                      type="button"
                      key={item}
                      className={active ? "active" : ""}
                      onClick={() => toggleSize(item)}
                    >
                      {item === "Free" ? "Free" : toFaText(item)}
                    </button>
                  );
                })}
              </div>

              <div className="size-finder-quick-actions">
                <button
                  type="button"
                  onClick={() =>
                    updateFilters((prev) => ({
                      ...prev,
                      size: commonSizes.join(","),
                      page: 1
                    }))
                  }
                >
                  انتخاب همه سایزها
                </button>

                <button type="button" onClick={clearSize} disabled={!selectedSizeValues.length}>
                  پاک کردن سایز
                </button>
              </div>
            </div>
          </div>

          <div className="shop-list-topbar size-finder-topbar">
            <div className="shop-result-meta">
              <span>{toPersianNumber(resultCount)} محصول</span>
              <strong>{title}</strong>
              <p>{subtitle}</p>
            </div>

            <select
              value={filters.sort}
              onChange={(e) => updateFilters((prev) => ({ ...prev, sort: e.target.value, page: 1 }))}
            >
              <option value="latest">جدیدترین</option>
              <option value="cheapest">ارزان‌ترین</option>
              <option value="expensive">گران‌ترین</option>
              <option value="popular">محبوب‌ترین</option>
              <option value="discount">بیشترین تخفیف</option>
            </select>
          </div>

          <div className="size-selected-summary size-selected-summary-modern">
            <div className="size-selected-summary-main">
              <span>فیلتر فعال</span>

              {selectedSizeValues.length > 0 ? (
                <div className="size-selected-chips">
                  {selectedSizeValues.map((item) => (
                    <button type="button" key={item} onClick={() => toggleSize(item)}>
                      {item === "Free" ? "Free" : toFaText(item)}
                      <FiX size={13} />
                    </button>
                  ))}
                </div>
              ) : (
                <strong>سایزی انتخاب نشده است</strong>
              )}

              {selectedCategoryTitle && <strong>دسته‌بندی: {selectedCategoryTitle}</strong>}
              {filters.available === "1" && <strong>فقط کالاهای موجود</strong>}
            </div>

            <div className="size-selected-summary-actions">
              <button type="button" onClick={() => setMobileFilterOpen(true)}>
                <FiSliders size={16} />
                فیلتر بیشتر
              </button>

              <button type="button" onClick={clearAll}>
                <FiRefreshCw size={15} />
                پاک کردن همه
              </button>

              <Link to="/products">مشاهده همه محصولات</Link>
            </div>
          </div>

          <div className="product-list-layout">
            <ProductFilters
              filters={filters}
              setFilters={updateFilters}
              filterOptions={data?.filters || {}}
              count={resultCount}
              open={mobileFilterOpen}
              setOpen={setMobileFilterOpen}
            />

            <section className="product-list-content">
              {!filters.size ? (
                <div className="shop-empty-state size-finder-empty-state-modern">
                  <FiSearch size={34} />
                  <strong>سایز انتخاب نشده است</strong>
                  <p>از بخش بالا یا گزینه «سایز من» در منو، یک یا چند سایز را انتخاب کن تا محصولات مناسب نمایش داده شوند.</p>
                </div>
              ) : isFetching ? (
                <ProductSkeleton />
              ) : hasProducts ? (
                <div className="product-grid">
                  {products.map((product) => (
                    <ProductCard product={product} key={product._id || product.id} />
                  ))}
                </div>
              ) : (
                <div className="shop-empty-state size-finder-empty-state-modern">
                  <FiSearch size={34} />
                  <strong>محصولی برای این انتخاب پیدا نشد</strong>
                  <p>ممکن است این سایزها در دسته یا فیلتر فعلی موجود نباشند. سایز کمتر انتخاب کن یا فیلترها را پاک کن.</p>

                  <div className="size-empty-actions">
                    <button type="button" onClick={clearAll}>
                      پاک کردن فیلترها
                    </button>

                    <Link to="/products">مشاهده همه محصولات</Link>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </section>
    </>
  );
};

export default SizeFinder;
